// ReferralWidget Component
// Displays referral code and referral history for students

import React, { useState, useEffect } from 'react';
import { getReferralCode, getMyReferrals } from '../../xpService';
import { FaGift, FaCheck } from 'react-icons/fa';

// Brand colors - matching StudentDashboard
const COLORS = {
  light: '#FFF8F1',
  dark: '#1A2341',
  orange: '#FF7A1A',
  orangeLight: '#f3b68a',
  blue: '#22305A',
  white: '#FFFFFF',
  gray: '#F5F7FA',
  textSecondary: '#64748B',
  borderLight: '#E2E8F0'
};

export default function ReferralWidget({ isArabic }) {
  const [referralCode, setReferralCode] = useState('');
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const isMobile = window.innerWidth <= 600;

  useEffect(() => {
    async function fetchData() {
      setLoading(true);

      // Fetch referral code
      const { data: codeData, error: codeError } = await getReferralCode();
      if (codeData && !codeError) {
        setReferralCode(codeData.referral_code);
      }

      // Fetch referral history
      const { data: referralsData, error: referralsError } = await getMyReferrals();
      if (referralsData && !referralsError) {
        setReferrals(referralsData.referrals || []);
      }

      setLoading(false);
    }

    fetchData();
  }, []);

  const handleCopy = () => {
    if (referralCode) {
      navigator.clipboard.writeText(referralCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div style={{
        background: COLORS.white,
        borderRadius: 20,
        padding: isMobile ? 20 : 24,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        border: `1px solid ${COLORS.borderLight}`,
        minHeight: isMobile ? 180 : 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ color: COLORS.textSecondary, opacity: 0.7 }}>
          {isArabic ? 'جاري التحميل...' : 'Loading...'}
        </div>
      </div>
    );
  }

  const signedUpReferrals = referrals.filter(r => r.status === 'signed_up' || r.status === 'rewarded');
  const totalXPEarned = signedUpReferrals.length * 200;

  return (
    <div style={{
      background: COLORS.white,
      borderRadius: 20,
      padding: isMobile ? 20 : 24,
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      border: `1px solid ${COLORS.borderLight}`,
      position: 'relative',
      overflow: 'hidden',
      minHeight: isMobile ? 180 : 200
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: 16
      }}>
        <FaGift style={{ fontSize: isMobile ? 32 : 40, marginRight: 12, color: COLORS.orange }} />
        <div>
          <div style={{
            fontSize: isMobile ? 16 : 18,
            color: COLORS.dark,
            fontWeight: 700
          }}>
            {isArabic ? 'دعوة الأصدقاء' : 'Invite Friends'}
          </div>
          <div style={{
            fontSize: isMobile ? 12 : 13,
            color: COLORS.textSecondary
          }}>
            {isArabic ? 'احصل على 200 XP لكل صديق' : 'Earn 200 XP per friend'}
          </div>
        </div>
      </div>

      {/* Referral Code Box */}
      <div style={{
        background: COLORS.gray,
        borderRadius: 12,
        padding: isMobile ? 12 : 16,
        marginBottom: 16,
        border: `2px dashed ${COLORS.borderLight}`
      }}>
        <div style={{
          fontSize: isMobile ? 11 : 12,
          color: COLORS.textSecondary,
          marginBottom: 8,
          textTransform: 'uppercase',
          letterSpacing: 0.5
        }}>
          {isArabic ? 'كود الإحالة الخاص بك' : 'Your Referral Code'}
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12
        }}>
          <div style={{
            fontSize: isMobile ? 20 : 24,
            fontWeight: 800,
            color: COLORS.orange,
            fontFamily: 'monospace',
            letterSpacing: 2
          }}>
            {referralCode || 'LOADING...'}
          </div>
          <button
            onClick={handleCopy}
            style={{
              background: copied ? '#4CAF50' : COLORS.orange,
              border: 'none',
              borderRadius: 8,
              padding: isMobile ? '8px 12px' : '10px 16px',
              color: COLORS.white,
              fontWeight: 700,
              fontSize: isMobile ? 12 : 13,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              if (!copied) {
                e.currentTarget.style.background = COLORS.orangeLight;
              }
            }}
            onMouseLeave={(e) => {
              if (!copied) {
                e.currentTarget.style.background = COLORS.orange;
              }
            }}
          >
            {copied ? (
              <>
                <FaCheck style={{ marginRight: 4 }} />
                {isArabic ? 'تم النسخ' : 'Copied'}
              </>
            ) : (isArabic ? 'نسخ' : 'Copy')}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{
        display: 'flex',
        gap: isMobile ? 12 : 16,
        marginBottom: 12
      }}>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: isMobile ? 11 : 12,
            color: COLORS.textSecondary,
            marginBottom: 4
          }}>
            {isArabic ? 'الأصدقاء المسجلون' : 'Friends Signed Up'}
          </div>
          <div style={{
            fontSize: isMobile ? 24 : 28,
            color: COLORS.dark,
            fontWeight: 700
          }}>
            {signedUpReferrals.length}
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: isMobile ? 11 : 12,
            color: COLORS.textSecondary,
            marginBottom: 4
          }}>
            {isArabic ? 'XP المكتسب' : 'XP Earned'}
          </div>
          <div style={{
            fontSize: isMobile ? 24 : 28,
            color: COLORS.orange,
            fontWeight: 700
          }}>
            {totalXPEarned}
          </div>
        </div>
      </div>

      {/* Share Instructions */}
      <div style={{
        background: COLORS.light,
        borderRadius: 8,
        padding: isMobile ? 10 : 12,
        fontSize: isMobile ? 11 : 12,
        color: COLORS.dark,
        lineHeight: 1.5
      }}>
        {isArabic
          ? 'شارك الكود مع أصدقائك. عندما يسجلون باستخدام كودك، ستحصل على 200 XP!'
          : 'Share this code with friends. When they sign up using your code, you earn 200 XP!'
        }
      </div>

      {/* Pending Referrals */}
      {referrals.length > 0 && (
        <div style={{
          marginTop: 16,
          paddingTop: 16,
          borderTop: `1px solid ${COLORS.borderLight}`
        }}>
          <div style={{
            fontSize: isMobile ? 12 : 13,
            color: COLORS.textSecondary,
            marginBottom: 8
          }}>
            {isArabic ? 'السجل' : 'History'}
          </div>
          <div style={{
            maxHeight: 120,
            overflowY: 'auto'
          }}>
            {referrals.slice(0, 5).map((ref, idx) => (
              <div key={idx} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '6px 0',
                fontSize: isMobile ? 11 : 12,
                color: COLORS.dark,
                borderBottom: idx < referrals.length - 1 ? `1px solid ${COLORS.borderLight}` : 'none'
              }}>
                <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {ref.referred_email}
                </div>
                <div style={{
                  padding: '2px 8px',
                  borderRadius: 6,
                  fontSize: 10,
                  fontWeight: 600,
                  background: ref.status === 'signed_up' || ref.status === 'rewarded' ? 'rgba(76, 175, 80, 0.2)' : COLORS.gray,
                  color: ref.status === 'signed_up' || ref.status === 'rewarded' ? '#4CAF50' : COLORS.textSecondary,
                  marginLeft: 8,
                  whiteSpace: 'nowrap'
                }}>
                  {ref.status === 'signed_up' || ref.status === 'rewarded' ? (
                    <>
                      <FaCheck style={{ marginRight: 4, fontSize: 10 }} />
                      {isArabic ? 'مسجل' : 'Signed Up'}
                    </>
                  ) : (isArabic ? 'معلق' : 'Pending')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
